import React from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { z } from 'zod';

import { aclosetRemoteConfigSchema } from '@looko-corp/acloset-config-schema';
import { acCaptureError, acFatal } from '@looko-corp/acloset-react-kit/src/sentry';

import { appLevelQueryClient } from '@/app/AppLevelQueryClientProvider';

/**
 * Remote Config 데이터 쿼리.
 */
const useAclosetRemoteConfigQuery = () => {
  return useQuery(
    {
      queryKey: ['acloset-remote-config'],
      queryFn: async () => {
        const v = await axios.get<unknown>(
          `${import.meta.env.VITE_PUBLIC_ACLOSET_REMOTE_CONFIG_URL}?_ts=${Date.now()}`,
          {
            responseType: 'json',
          },
        );
        const result = await aclosetRemoteConfigSchema.safeParseAsync(v.data);

        if (result.success === true) {
          return result.data;
        } else {
          acCaptureError('Failed to parse remote config', {
            extra: {
              ...result.error,
            },
          });
          return v.data as z.output<typeof aclosetRemoteConfigSchema>;
        }
      },
      gcTime: 24 * 60 * 60 * 1000,
      staleTime: 24 * 60 * 60 * 1000,
      retry: 3,
      refetchOnWindowFocus: true,
    },
    appLevelQueryClient,
  );
};

/**
 * Remote Config 데이터.
 *
 * NOTE: 최상단에서 데이터를 보존하므로 반드시 있어야 합니다.
 */
export const useAclosetRemoteConfig = () => {
  const { data } = useAclosetRemoteConfigQuery();
  return data!;
};

/**
 * Remote Config 초기화 이펙트.
 *
 * 앱 최상단에서 초기화 하여, 장기간 캐시 시킵니다.
 */
export const useInitializeAclosetRemoteConfig = (networkConnected: boolean) => {
  const { isPending, isSuccess, error } = useAclosetRemoteConfigQuery();

  React.useEffect(() => {
    if (networkConnected && !isPending && !isSuccess) {
      acFatal('Failed to get remote config.', JSON.stringify(error));
    }
  }, [error, isPending, isSuccess, networkConnected]);

  return { isPending, isSuccess };
};
